import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";

const dayIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const COLORS = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", bar: "bg-blue-500" },
  green: { bg: "bg-emerald-50", text: "text-emerald-700", bar: "bg-emerald-500" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", bar: "bg-purple-500" },
};

const CARDS = [
  { key: "login_conversion", color: "blue", titlu: "Rata conversie login", descriere: "Cati dintre vizitatorii paginii de login se logheaza efectiv (auth + guest)." },
  { key: "oferte_per_logat", color: "green", titlu: "Oferte per logat", descriere: "Cate oferte (toate tipurile) genereaza utilizatorii logati." },
  { key: "logati_per_pagina", color: "purple", titlu: "Pagini per logat", descriere: "Media de pagini vizitate per logat (auth + guest). Valoare = pagini / logati." },
];

function Row({ label, value, small }) {
  return (
    <div className={`flex justify-between ${small ? "text-xs text-slate-500" : ""}`}>
      <span>{label}</span>
      <span className={small ? "" : "font-medium text-slate-800"}>{value}</span>
    </div>
  );
}

function RateCard({ titlu, descriere, rate, color }) {
  if (!rate) return null;
  const isRatio = rate.format === "ratio";
  const barPct = isRatio ? Math.min(100, rate.procent * 5) : Math.min(100, rate.procent);
  const c = COLORS[color];
  return (
    <div className="rounded border border-slate-200 bg-white p-5" data-testid={`rata-card-${color}`}>
      <h3 className="text-sm font-semibold text-slate-700 mb-1">{titlu}</h3>
      <p className="text-xs text-slate-500 mb-3">{descriere}</p>
      <div className={`${c.bg} rounded p-4 mb-3`}>
        <div className="flex items-baseline justify-between">
          <span className={`text-3xl font-bold ${c.text}`} data-testid={`rata-value-${color}`}>{rate.procent}{isRatio ? "" : "%"}</span>
          <span className="text-sm text-slate-600">{rate.numarator} / {rate.numitor}</span>
        </div>
        <div className="mt-2 w-full bg-white rounded-full h-2 overflow-hidden">
          <div className={`h-2 ${c.bar}`} style={{ width: `${barPct}%` }} />
        </div>
      </div>
      <div className="text-xs text-slate-600 space-y-1">
        <Row label={rate.numarator_label} value={rate.numarator} />
        <Row label={rate.numitor_label} value={rate.numitor} />
        {rate.breakdown ? (
          <div className="pt-2 mt-2 border-t border-slate-200 space-y-1">
            {Object.entries(rate.breakdown).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} value={v} small />)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function RataSucces() {
  const [f, setF] = useState({ date_from: dayIso(29), date_to: dayIso(0) });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!f.date_from || !f.date_to) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/configurator/rata-succes", { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="Rata succes">
      <div data-testid="rata-succes-page" className="space-y-4">
        <p className="text-sm text-slate-500">Rate de conversie pentru intervalul selectat (exclus admin si conturi interne).</p>
        <div className="rounded border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-slate-600">Data de la
            <input type="date" value={f.date_from} max={f.date_to} onChange={(e) => setF({ ...f, date_from: e.target.value })}
              className="mt-1 block h-9 px-3 rounded border border-slate-300 text-sm" data-testid="rata-date-from" />
          </label>
          <label className="text-xs font-medium text-slate-600">Data pana la
            <input type="date" value={f.date_to} min={f.date_from} max={dayIso(0)} onChange={(e) => setF({ ...f, date_to: e.target.value })}
              className="mt-1 block h-9 px-3 rounded border border-slate-300 text-sm" data-testid="rata-date-to" />
          </label>
          <span className="text-xs text-slate-500" data-testid="rata-total-zile">{data?.total_zile ? `${data.total_zile} zile` : ""}</span>
          {loading ? <Loader2 size={16} className="animate-spin text-slate-400" data-testid="rata-loading" /> : null}
        </div>
        {error ? <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="rata-error">{error}</div> : null}
        {data?.rate ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CARDS.map((c) => <RateCard key={c.key} {...c} rate={data.rate[c.key]} />)}
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
